import { useContext, useState, useEffect } from "react";
import {
  Box,
  Stack,
  TextField,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PlannerContext } from "../../../../context/planner/PlannerContext";
import Button from "../../../input/button";
import { TimeRangeFields, isTimeRangeValid, TaskPicker } from "./shared";

const EditTaskBlock = ({ block }) => {
  const {
    updateBlock,
    deleteBlock,
    tasks: allTasks,
    blocks,
    setSelectedBlockId,
    setSelectedTool,
    closeToolPanel,
  } = useContext(PlannerContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [name, setName] = useState(block.name || "Tasks");
  const [taskIds, setTaskIds] = useState(block.taskIds || []);
  const [startTime, setStartTime] = useState(block.startTime);
  const [endTime, setEndTime] = useState(block.endTime);
  const [hasChanges, setHasChanges] = useState(false);
  const [taskSearchQuery, setTaskSearchQuery] = useState("");

  // Reset form when block changes
  useEffect(() => {
    setName(block.name || "Tasks");
    setTaskIds(block.taskIds || []);
    setStartTime(block.startTime);
    setEndTime(block.endTime);
    setHasChanges(false);
  }, [block.id, block.name, block.taskIds, block.startTime, block.endTime]);

  // Track changes
  useEffect(() => {
    const taskIdsChanged =
      JSON.stringify([...taskIds].sort()) !==
      JSON.stringify([...(block.taskIds || [])].sort());
    const changed =
      name !== (block.name || "Tasks") ||
      taskIdsChanged ||
      startTime !== block.startTime ||
      endTime !== block.endTime;
    setHasChanges(changed);
  }, [name, taskIds, startTime, endTime, block]);

  const handleSave = () => {
    updateBlock(block.id, {
      name,
      taskIds,
      startTime,
      endTime,
    });
    setHasChanges(false);
    setSelectedBlockId(null);
    setSelectedTool("dayConfig");
    if (isMobile) {
      closeToolPanel();
    }
  };

  const handleDelete = () => {
    deleteBlock(block.id);
    if (isMobile) {
      closeToolPanel();
    }
  };

  const toggleTask = (taskId) => {
    setTaskIds((prev) => {
      if (prev.includes(taskId)) {
        return prev.filter((id) => id !== taskId);
      }
      return [...prev, taskId];
    });
  };

  const timesValid = isTimeRangeValid(startTime, endTime);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        flex: 1,
        mt: 0,
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
          pt: 1,
          pb: 2,
        }}
      >
        <Stack spacing={1} sx={{ py: 2 }}>
          <TextField
            label="Block Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: "4px !important" }}
          />

          <TimeRangeFields
            startTime={startTime}
            endTime={endTime}
            onStartTimeChange={setStartTime}
            onEndTimeChange={setEndTime}
            gap={1}
          />

          <Divider />

          <Typography variant="subtitle2">
            Tasks ({taskIds.length} selected)
          </Typography>

          <TaskPicker
            tasks={allTasks}
            selectedTaskIds={taskIds}
            onTaskToggle={toggleTask}
            searchQuery={taskSearchQuery}
            onSearchChange={setTaskSearchQuery}
            headerText=""
            blocksData={blocks}
            currentBlockId={block.id}
          />

          {!isMobile && (
            <>
              <Button
                text="Save Changes"
                onClick={handleSave}
                color="orange"
                disabled={!hasChanges || !timesValid}
                fullWidth
              />

              <Divider />

              <Button
                text="Delete Block"
                onClick={handleDelete}
                color="red"
                icon="trash"
                fullWidth
              />
            </>
          )}
        </Stack>
      </Box>

      {isMobile && (
        <Box
          sx={{
            pt: 2,
            pb: 1,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Button
            text="Save Changes"
            onClick={handleSave}
            color="orange"
            disabled={!hasChanges || !timesValid}
            fullWidth
          />
          <Button
            text="Delete Block"
            onClick={handleDelete}
            color="red"
            icon="trash"
            fullWidth
          />
        </Box>
      )}
    </Box>
  );
};

export default EditTaskBlock;
